import {
  Button,
  ButtonIcon,
  ButtonSpinner,
  ButtonText,
} from "@/components/ui/button";
import {
  FormControl,
  FormControlError,
  FormControlErrorIcon,
  FormControlErrorText,
  FormControlHelper,
  FormControlHelperText,
  FormControlLabel,
  FormControlLabelText,
} from "@/components/ui/form-control";
import { Heading } from "@/components/ui/heading";
import { Text } from "@/components/ui/text";
import { ChevronDownIcon, CloseIcon, Icon } from "@/components/ui/icon";
import { Input, InputField } from "@/components/ui/input";
import { Menu, MenuItem, MenuItemLabel } from "@/components/ui/menu";
import {
  ModalBackdrop,
  ModalContent,
  ModalHeader,
  Modal,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
} from "@/components/ui/modal";
import {
  Select,
  SelectBackdrop,
  SelectContent,
  SelectDragIndicator,
  SelectDragIndicatorWrapper,
  SelectIcon,
  SelectInput,
  SelectItem,
  SelectPortal,
  SelectTrigger,
} from "@/components/ui/select";
import {
  getUserControllerMeQueryKey,
  useUserControllerUpdate,
  useUserControllerUploadAvatar,
} from "@/generated/api/api";
import { UserDTO } from "@/generated/api/models";
import { ImagePicker } from "@/screens/image-picker/image.picker";
import { useToast } from "@/utils/useToast";
import { InterfaceMenuProps } from "@gluestack-ui/menu/lib/types";
import { InterfaceModalProps } from "@gluestack-ui/modal/lib/types";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  Calendar,
  Camera,
  Edit,
  Landmark,
  Mail,
  Trash2,
  Upload,
} from "lucide-react-native";
import { useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { Keyboard, Platform } from "react-native";
import { z } from "zod";
import { ProfileBeneficiaryModalEdit } from "./beneficiary.modal";
import { useLocalSearchParams, useRouter } from "expo-router";
import { uriToBlob } from "@/utils/uri.to.blob";
import * as Linking from "expo-linking";
import {
  Actionsheet,
  ActionsheetBackdrop,
  ActionsheetContent,
  ActionsheetDragIndicator,
  ActionsheetDragIndicatorWrapper,
  ActionsheetIcon,
  ActionsheetItem,
  ActionsheetItemText,
} from "@/components/ui/actionsheet";
import { InterfaceActionsheetProps } from "@gluestack-ui/actionsheet/lib/types";

export function ProfilePayoutActionSheet({
  user,
  onClose,
  ...rest
}: InterfaceMenuProps & {
  user: UserDTO;
}) {
  const [ibanIsOpened, setIbanIsOpened] = useState(false);
  const [frequencyIsOpened, setFrequencyIsOpened] = useState(false);

  return (
    <>
      <Menu placement="bottom" offset={5} onClose={onClose} {...rest}>
        <MenuItem
          key="iban"
          textValue="Update IBAN"
          onPress={() => setIbanIsOpened(true)}
        >
          <Icon as={Landmark} size="sm" className="mr-2" />
          <MenuItemLabel size="sm">Update IBAN</MenuItemLabel>
        </MenuItem>
        <MenuItem
          key="frequency"
          textValue="Update frequency"
          onPress={() => setFrequencyIsOpened(true)}
        >
          <Icon as={Calendar} size="sm" className="mr-2" />
          <MenuItemLabel size="sm">Update frequency</MenuItemLabel>
        </MenuItem>
      </Menu>
      <ProfilePayoutFrequencyModalEdit
        isOpen={frequencyIsOpened}
        onClose={() => setFrequencyIsOpened(false)}
        user={user}
      />
      <ProfileBeneficiaryModalEdit
        isOpen={ibanIsOpened}
        onClose={() => setIbanIsOpened(false)}
        user={user}
      />
      {/* <ProfileNameModalEdit
        isOpen={editIsOpened}
        onClose={() => setEditIsOpened(false)}
        user={user}
      /> */}
    </>
  );
}

const PayoutFrequencySchema = z.object({
  payoutFrequency: z.union(
    [z.literal("DAILY"), z.literal("WEEKLY"), z.literal("MONTHLY")],
    { message: "Payout frequency is required" }
  ),
});
type PayoutFrequencySchemaType = z.infer<typeof PayoutFrequencySchema>;

export function ProfilePayoutFrequencyModalEdit(
  props: { user: UserDTO } & Pick<InterfaceModalProps, "isOpen" | "onClose">
) {
  const defaultValues = useMemo(
    () => ({
      payoutFrequency: props.user.payoutFrequency ?? undefined,
    }),
    [props.user.payoutFrequency]
  );

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<PayoutFrequencySchemaType>({
    resolver: zodResolver(PayoutFrequencySchema),
    defaultValues,
  });

  useEffect(() => {
    reset(defaultValues);
  }, [props.user]);

  const queryClient = useQueryClient();
  const { showToast } = useToast();

  const { mutateAsync } = useUserControllerUpdate({
    mutation: {
      onSuccess: (data) => {
        showToast("success", "Payout frequency has been successfully updated");
        queryClient.setQueryData(
          getUserControllerMeQueryKey(),
          (old: UserDTO | undefined) => {
            if (!old) {
              return;
            }

            return {
              ...old,
              ...data,
            };
          }
        );
        reset({
          payoutFrequency: data.payoutFrequency ?? undefined,
        });
        props.onClose?.();
      },
    },
  });

  const onSubmit = (data: PayoutFrequencySchemaType) => {
    return mutateAsync({
      data: {
        ...props.user,
        ...data,
      },
    }).catch(() => {});
  };

  return (
    <Modal avoidKeyboard isOpen={props.isOpen} onClose={props.onClose}>
      <ModalBackdrop />
      <ModalContent>
        <ModalHeader>
          <Heading>Update payout frequency</Heading>
          <ModalCloseButton>
            <Icon
              as={CloseIcon}
              size="md"
              className="stroke-background-400 group-[:hover]/modal-close-button:stroke-background-700 group-[:active]/modal-close-button:stroke-background-900 group-[:focus-visible]/modal-close-button:stroke-background-900"
            />
          </ModalCloseButton>
        </ModalHeader>
        <ModalBody className="mb-4" contentContainerClassName="gap-4">
          <FormControl
            isInvalid={!!errors?.payoutFrequency}
            className="w-full"
            isRequired
          >
            <FormControlLabel>
              <FormControlLabelText>Payout frequency</FormControlLabelText>
            </FormControlLabel>
            <Controller
              defaultValue={props.user.payoutFrequency}
              name="payoutFrequency"
              control={control}
              render={({ field: { onChange, onBlur, value } }) => (
                <Select onValueChange={onChange} selectedValue={value}>
                  <SelectTrigger>
                    <SelectInput
                      placeholder="Select payout frequency"
                      className="flex-1"
                      onBlur={onBlur}
                    />
                    <SelectIcon className="mr-3" as={ChevronDownIcon} />
                  </SelectTrigger>
                  <SelectPortal>
                    <SelectBackdrop />
                    <SelectContent>
                      <SelectDragIndicatorWrapper>
                        <SelectDragIndicator />
                      </SelectDragIndicatorWrapper>
                      <SelectItem label="DAILY" value="DAILY" />
                      <SelectItem label="WEEKLY" value="WEEKLY" />
                      <SelectItem label="MONTHLY" value="MONTHLY" />
                    </SelectContent>
                  </SelectPortal>
                </Select>
              )}
            />
            <FormControlError>
              <FormControlErrorIcon as={AlertTriangle} />
              <FormControlErrorText>
                {errors?.payoutFrequency?.message}
              </FormControlErrorText>
            </FormControlError>
          </FormControl>
        </ModalBody>
        <ModalFooter className="flex-col items-start">
          <Button
            className="w-full gap-2"
            onPress={handleSubmit(onSubmit)}
            isDisabled={isSubmitting}
          >
            <ButtonText>Save</ButtonText>
            {isSubmitting && <ButtonSpinner />}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export function ProfileActionSheet({
  user,
  onClose,
  ...rest
}: InterfaceMenuProps & {
  user: UserDTO;
}) {
  const [editIsOpened, setEditIsOpened] = useState(false);
  const [avatarIsOpened, setAvatarIsOpened] = useState<
    "camera" | "library" | undefined
  >();

  const { mutateAsync: uploadImage } = useUserControllerUploadAvatar();
  const queryClient = useQueryClient();

  const { "#": hash } = useLocalSearchParams<{ "#": string }>();
  const router = useRouter();

  useEffect(() => {
    if (hash === "name") {
      setEditIsOpened(true);
      router.setParams({ "#": "" });
    }
  }, [hash]);

  return (
    <>
      <Menu placement="bottom" offset={5} onClose={onClose} {...rest}>
        <MenuItem
          key="name"
          textValue="Edit name"
          onPress={() => setEditIsOpened(true)}
        >
          <Icon as={Edit} size="sm" className="mr-2" />
          <MenuItemLabel size="sm">Edit name</MenuItemLabel>
        </MenuItem>
        <MenuItem
          key="avatar"
          textValue="Upload avatar"
          onPress={() => setAvatarIsOpened("library")}
        >
          <Icon as={Upload} size="sm" className="mr-2" />
          <MenuItemLabel size="sm">
            {Platform.OS === "web"
              ? "Upload Avatar"
              : "Choose Avatar from Library"}
          </MenuItemLabel>
        </MenuItem>
        {(Platform.OS === "android" || Platform.OS === "ios") && (
          <MenuItem
            key="avatar_photo"
            textValue="Take Avatar Photo"
            onPress={() => setAvatarIsOpened("camera")}
          >
            <Icon as={Camera} size="sm" className="mr-2" />
            <MenuItemLabel size="sm">Take Avatar Photo</MenuItemLabel>
          </MenuItem>
        )}
      </Menu>
      <ProfileNameModalEdit
        isOpen={editIsOpened}
        onClose={() => setEditIsOpened(false)}
        user={user}
      />
      {avatarIsOpened && (
        <ImagePicker
          isOpened={!!avatarIsOpened}
          isCamera={avatarIsOpened === "camera"}
          type="avatar"
          onClose={(img) => {
            setAvatarIsOpened(undefined);
            if (!img) {
              return;
            }

            const upload = (uriOrBlob: any) =>
              uploadImage(
                {
                  data: {
                    file: uriOrBlob,
                  },
                },
                {
                  onSuccess: () => {
                    queryClient.invalidateQueries({
                      queryKey: getUserControllerMeQueryKey(),
                    });
                  },
                }
              ).catch((e) => console.error(e));

            uriToBlob(img.uri).then(upload);
          }}
        />
      )}
    </>
  );
}

const NameSchema = z.object({
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
});
type NameSchemaType = z.infer<typeof NameSchema>;

export function ProfileNameModalEdit(
  props: { user: UserDTO } & Pick<InterfaceModalProps, "isOpen" | "onClose">
) {
  const defaultValues = useMemo(
    () => ({
      firstName: props.user.firstName ?? undefined,
      lastName: props.user.lastName ?? undefined,
    }),
    [props.user.lastName, props.user.firstName]
  );

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<NameSchemaType>({
    resolver: zodResolver(NameSchema),
    defaultValues,
  });

  useEffect(() => {
    reset(defaultValues);
  }, [props.user]);

  const queryClient = useQueryClient();
  const { showToast } = useToast();

  const { mutateAsync } = useUserControllerUpdate({
    mutation: {
      onSuccess: (data) => {
        showToast("success", "Profile has been successfully updated");
        queryClient.setQueryData(
          getUserControllerMeQueryKey(),
          (old: UserDTO | undefined) => {
            if (!old) {
              return;
            }

            return {
              ...old,
              ...data,
            };
          }
        );
        reset({
          firstName: data.firstName ?? undefined,
          lastName: data.lastName ?? undefined,
        });
        props.onClose?.();
      },
    },
  });

  const onSubmit = (data: NameSchemaType) => {
    return mutateAsync({
      data: {
        ...props.user,
        ...data,
      },
    }).catch(() => {});
  };

  const handleKeyPress = () => {
    Keyboard.dismiss();
    handleSubmit(onSubmit)();
  };

  return (
    <Modal avoidKeyboard isOpen={props.isOpen} onClose={props.onClose}>
      <ModalBackdrop />
      <ModalContent>
        <ModalHeader>
          <Heading>Edit name</Heading>
          <ModalCloseButton>
            <Icon
              as={CloseIcon}
              size="md"
              className="stroke-background-400 group-[:hover]/modal-close-button:stroke-background-700 group-[:active]/modal-close-button:stroke-background-900 group-[:focus-visible]/modal-close-button:stroke-background-900"
            />
          </ModalCloseButton>
        </ModalHeader>
        <ModalBody className="mb-4" contentContainerClassName="gap-4">
          <FormControl
            isInvalid={!!errors?.firstName}
            className="w-full"
            isRequired
          >
            <FormControlLabel>
              <FormControlLabelText>First name</FormControlLabelText>
            </FormControlLabel>
            <Controller
              defaultValue=""
              name="firstName"
              control={control}
              render={({ field: { onChange, onBlur, value } }) => (
                <Input>
                  <InputField
                    placeholder="First name"
                    value={value}
                    onChangeText={onChange}
                    onBlur={onBlur}
                    onSubmitEditing={handleKeyPress}
                    returnKeyType="done"
                  />
                </Input>
              )}
            />
            <FormControlError>
              <FormControlErrorIcon as={AlertTriangle} />
              <FormControlErrorText>
                {errors?.firstName?.message}
              </FormControlErrorText>
            </FormControlError>
          </FormControl>
          <FormControl isInvalid={!!errors?.lastName} className="w-full">
            <FormControlLabel>
              <FormControlLabelText>Last name</FormControlLabelText>
            </FormControlLabel>
            <Controller
              defaultValue=""
              name="lastName"
              control={control}
              render={({ field: { onChange, onBlur, value } }) => (
                <Input>
                  <InputField
                    placeholder="Last name"
                    value={value}
                    onChangeText={onChange}
                    onBlur={onBlur}
                    onSubmitEditing={handleKeyPress}
                    returnKeyType="done"
                  />
                </Input>
              )}
            />
            <FormControlError>
              <FormControlErrorIcon as={AlertTriangle} />
              <FormControlErrorText>
                {errors?.lastName?.message}
              </FormControlErrorText>
            </FormControlError>
          </FormControl>
        </ModalBody>
        <ModalFooter className="flex-col items-start">
          <Button
            className="w-full gap-2"
            onPress={handleSubmit(onSubmit)}
            isDisabled={isSubmitting}
          >
            <ButtonText>Save</ButtonText>
            {isSubmitting && <ButtonSpinner />}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

const ProfileQRCodeSchema = z.object({
  displayName: z.string().optional(),
  collectingGoal: z.string().optional(),
});

type ProfileQRCodeSchemaType = z.infer<typeof ProfileQRCodeSchema>;

export function ProfileQRCodeModalEdit(
  props: { user: UserDTO } & Pick<InterfaceModalProps, "isOpen" | "onClose">
) {
  const defaultValues = useMemo(
    () => ({
      displayName: props.user.displayName ?? undefined,
      collectingGoal: props.user.collectingGoal ?? undefined,
    }),
    [props.user.displayName, props.user.collectingGoal]
  );

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<ProfileQRCodeSchemaType>({
    resolver: zodResolver(ProfileQRCodeSchema),
    defaultValues,
  });

  useEffect(() => {
    reset(defaultValues);
  }, [props.user]);

  const queryClient = useQueryClient();
  const { showToast } = useToast();

  const { mutateAsync } = useUserControllerUpdate({
    mutation: {
      onSuccess: (data) => {
        showToast("success", "Profile has been successfully updated");
        queryClient.setQueryData(
          getUserControllerMeQueryKey(),
          (old: UserDTO | undefined) => {
            if (!old) {
              return;
            }

            return {
              ...old,
              ...data,
            };
          }
        );
        reset({
          displayName: data.displayName ?? undefined,
          collectingGoal: data.collectingGoal ?? undefined,
        });
        props.onClose?.();
      },
    },
  });

  const onSubmit = (data: ProfileQRCodeSchemaType) => {
    return mutateAsync({
      data: {
        ...props.user,
        ...data,
      },
    }).catch(() => {});
  };

  const handleKeyPress = () => {
    Keyboard.dismiss();
    handleSubmit(onSubmit)();
  };

  return (
    <Modal avoidKeyboard isOpen={props.isOpen} onClose={props.onClose}>
      <ModalBackdrop />
      <ModalContent>
        <ModalHeader>
          <Heading>Update QR-code info</Heading>
          <ModalCloseButton>
            <Icon
              as={CloseIcon}
              size="md"
              className="stroke-background-400 group-[:hover]/modal-close-button:stroke-background-700 group-[:active]/modal-close-button:stroke-background-900 group-[:focus-visible]/modal-close-button:stroke-background-900"
            />
          </ModalCloseButton>
        </ModalHeader>
        <ModalBody className="mb-4" contentContainerClassName="gap-4">
          <FormControl isInvalid={!!errors?.displayName} className="w-full">
            <FormControlLabel>
              <FormControlLabelText>Display name</FormControlLabelText>
            </FormControlLabel>
            <Controller
              defaultValue=""
              name="displayName"
              control={control}
              render={({ field: { onChange, onBlur, value } }) => (
                <Input>
                  <InputField
                    placeholder="Display name on QR-code page"
                    value={value}
                    onChangeText={onChange}
                    onBlur={onBlur}
                    onSubmitEditing={handleKeyPress}
                    returnKeyType="done"
                  />
                </Input>
              )}
            />
            <FormControlHelper>
              <FormControlHelperText>
                Shows to tippers. If not set - will be displayed your first and
                last name
              </FormControlHelperText>
            </FormControlHelper>
            <FormControlError>
              <FormControlErrorIcon as={AlertTriangle} />
              <FormControlErrorText>
                {errors?.displayName?.message}
              </FormControlErrorText>
            </FormControlError>
          </FormControl>
          <FormControl isInvalid={!!errors?.collectingGoal} className="w-full">
            <FormControlLabel>
              <FormControlLabelText>Collecting goal</FormControlLabelText>
            </FormControlLabel>
            <Controller
              defaultValue=""
              name="collectingGoal"
              control={control}
              render={({ field: { onChange, onBlur, value } }) => (
                <Input>
                  <InputField
                    placeholder="Collecting goal on QR-code page"
                    value={value}
                    onChangeText={onChange}
                    onBlur={onBlur}
                    onSubmitEditing={handleKeyPress}
                    returnKeyType="done"
                  />
                </Input>
              )}
            />
            <FormControlHelper>
              <FormControlHelperText>
                You may put something meaningful here for tippers, which would
                make them to contribute to collecting goal.
              </FormControlHelperText>
            </FormControlHelper>
            <FormControlError>
              <FormControlErrorIcon as={AlertTriangle} />
              <FormControlErrorText>
                {errors?.collectingGoal?.message}
              </FormControlErrorText>
            </FormControlError>
          </FormControl>
        </ModalBody>
        <ModalFooter className="flex-col items-start">
          <Button
            className="w-full gap-2"
            onPress={handleSubmit(onSubmit)}
            isDisabled={isSubmitting}
          >
            <ButtonText>Save</ButtonText>
            {isSubmitting && <ButtonSpinner />}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export function ProfileManageAccountSheet(props: InterfaceActionsheetProps) {
  const [deleteIsOpened, setDeleteIsOpened] = useState(false);

  return (
    <>
      <Actionsheet closeOnOverlayClick {...props}>
        <ActionsheetBackdrop />
        <ActionsheetContent>
          <ActionsheetDragIndicatorWrapper>
            <ActionsheetDragIndicator />
          </ActionsheetDragIndicatorWrapper>
          <ActionsheetItem
            onPress={() => {
              props.onClose?.();
              setDeleteIsOpened(true);
            }}
          >
            <ActionsheetIcon className="stroke-background-700" as={Trash2} />
            <ActionsheetItemText className="text-red-500">
              Delete my account
            </ActionsheetItemText>
          </ActionsheetItem>
        </ActionsheetContent>
      </Actionsheet>
      <ProfileDeleteAccountModal
        onClose={() => setDeleteIsOpened(false)}
        isOpen={deleteIsOpened}
      />
    </>
  );
}

export function ProfileDeleteAccountModal(
  props: Pick<InterfaceModalProps, "isOpen" | "onClose">
) {
  return (
    <Modal avoidKeyboard isOpen={props.isOpen} onClose={props.onClose}>
      <ModalBackdrop />
      <ModalContent>
        <ModalHeader>
          <Heading>Delete account</Heading>
          <ModalCloseButton>
            <Icon
              as={CloseIcon}
              size="md"
              className="stroke-background-400 group-[:hover]/modal-close-button:stroke-background-700 group-[:active]/modal-close-button:stroke-background-900 group-[:focus-visible]/modal-close-button:stroke-background-900"
            />
          </ModalCloseButton>
        </ModalHeader>
        <ModalBody className="mb-4" contentContainerClassName="gap-4">
          <Text>
            To proceed with account deletion, please contact our support team at{" "}
            <Text bold>contact@gotips.co</Text>. We're here to help and will
            handle your request as soon as possible.
          </Text>
        </ModalBody>
        <ModalFooter className="items-end">
          <Button
            variant="outline"
            action="secondary"
            onPress={() => {
              props.onClose?.();
            }}
          >
            <ButtonText>Cancel</ButtonText>
          </Button>
          <Button
            onPress={() => {
              Linking.openURL(
                "mailto:contact@gotips.co?subject=Account%20deletion%20request&body=Hello%2C%0A%0AI%20would%20like%20to%20request%20the%20permanent%20deletion%20of%20my%20GoTips%20account.%0A%0AHere%20are%20my%20details%3A%0A%E2%80%93%20Full%20Name%3A%20%5BYour%20Name%5D%0A%E2%80%93%20Email%20used%20on%20GoTips%3A%20%5BYour%20Email%5D%0A%0AI%20understand%20that%20this%20action%20is%20permanent%20and%20cannot%20be%20undone.%0A%0AThank%20you."
              );
              props.onClose?.();
            }}
          >
            <ButtonIcon as={Mail} />
            <ButtonText>Open Mail</ButtonText>
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
